import { Dialog, ProgressBar, useToast } from '@sl/react-datafabric-components';
import { FC, useEffect, useState } from 'react';
import { useAgentContext } from '../../../../common/context/agent.provider';
import { useBuyTokensMutation } from '../../../../common/redux/services/subscriptions';

interface VerifyTokenPurchaseDialogProps {
  visible: boolean;
  onHide: () => void;
  transaction: { transactionId: string; sessionId?: string };
  onPurchaseComplete: () => void; // Callback for success/failure
}

export const VerifyTokenPurchaseDialog: FC<VerifyTokenPurchaseDialogProps> = ({
  visible,
  onHide,
  transaction,
  onPurchaseComplete,
}) => {
  const { showToast } = useToast();
  const { currentAgentId } = useAgentContext();

  const [
    finalizePurchase,
    {
      isLoading: isFinalizing,
      isSuccess: finalizeSuccess,
      isError: finalizeErrorOccurred,
      error: finalizeApiErrorData,
      reset: resetFinalizeMutation,
    },
  ] = useBuyTokensMutation(); // Use your actual mutation here
  const [hasTriggeredFinalization, setHasTriggeredFinalization] =
    useState(false);

  useEffect(() => {
    // Trigger the finalization API call
    if (visible && transaction?.transactionId && !hasTriggeredFinalization) {
      finalizePurchase({ agentID: currentAgentId, ...transaction });
      setHasTriggeredFinalization(true);
    }
  }, [visible, transaction, finalizePurchase, hasTriggeredFinalization]);

  // Separate effect for resetting mutation state and local flag when dialog is hidden
  useEffect(() => {
    // Clean up state when dialog is hidden
    if (!visible) {
      resetFinalizeMutation();
      setHasTriggeredFinalization(false);
    }
  }, [visible, resetFinalizeMutation]);

  useEffect(() => {
    // Call onPurchaseComplete only once after finalization attempt
    if (
      hasTriggeredFinalization &&
      (finalizeSuccess || finalizeErrorOccurred)
    ) {
      onPurchaseComplete(); // Notify parent (ManageSubscriptionPage) even on error
    }
  }, [
    finalizeSuccess,
    finalizeErrorOccurred,
    finalizeApiErrorData,
    showToast,
    hasTriggeredFinalization, // Ensure this runs after finalization has been attempted
    onPurchaseComplete,
  ]);

  const handleClose = () => {
    // Only allow closing if not currently finalizing
    if (!isFinalizing) {
      onHide();
    }
  };

  return (
    <Dialog
      header={
        isFinalizing
          ? 'Verifying Payment'
          : finalizeSuccess
          ? 'Purchase Complete'
          : 'Purchase Status'
      }
      visible={visible}
      style={{ width: '350px' }}
      onHide={handleClose}
      closable={!isFinalizing} // Prevent closing via X button while finalizing
    >
      <div
        className='flex flex-column align-items-center text-center'
        style={{ gap: '1rem' }}
      >
        {isFinalizing && (
          <>
            <p>
              Verifying your payment and provisioning credits, please wait...
            </p>
            <ProgressBar
              mode='indeterminate'
              style={{ height: '6px', width: '100%' }}
            />
          </>
        )}
        {finalizeSuccess && (
          <p>
            <i
              className='pi pi-check-circle text-green-500 mr-2'
              style={{ fontSize: '1.5rem' }}
            ></i>
            Your credit purchase was successful!
          </p>
        )}
        {finalizeErrorOccurred && (
          <p>
            <i
              className='pi pi-times-circle text-red-500 mr-2'
              style={{ fontSize: '1.5rem' }}
            ></i>
            There was an error completing your purchase: {`Unknown error`}
          </p>
        )}
        {!isFinalizing && !finalizeSuccess && !finalizeErrorOccurred && (
          <p>Waiting for verification...</p> // Should not happen if triggered correctly
        )}
      </div>
    </Dialog>
  );
};
